// src/pages/Captures.tsx - 更新為使用 useCaptures hook
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHeader } from '../hooks/useHeader';
import { useTipAutoMarker } from '../features/onboarding';
import { Capture } from '../types/capture';
import { getCaptureById } from '../services/api/captures'; // 只保留單個 capture 獲取
import { getCurrentUserId } from '../services/auth';
import { ArrowLeft } from '../components/common/icons';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Lesson1Guide } from '../features/onboarding/components/Lesson1Guide';
import CaptureModal from '../components/captures/CaptureModal';
import CaptureTimelineView from '../components/captures/CaptureTimelineView';
import CaptureFilterBar from '../components/captures/CaptureFilterBar';
import CaptureInputSection from '../components/captures/CaptureInputSection';
import { NotebookPen, Plus } from '../components/common/icons';
import { useCaptures } from '../hooks/useCaptures'; // 使用新的 hook
import { useToast } from '../contexts/ToastContext';
import { logError } from '../utils/logger';
import ErrorState from '../components/common/ErrorState';


const Captures: React.FC = () => {
  const { showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Lesson 引導模式：從 LessonsPanel 導航過來時帶有 lessonId state
  const lessonId = (location.state as { lessonId?: string } | null)?.lessonId;
  const [showLesson1Guide, setShowLesson1Guide] = useState(lessonId === 'lesson-1');
  const [lessonSeedCaptureId, setLessonSeedCaptureId] = useState<string | null>(null);
  const captureId = searchParams.get('captureId');
  useTipAutoMarker();

  const userId = getCurrentUserId();

  // 使用增強版 useCaptures hook
  const {
    captures, // 全部 Capture 數據
    isLoading,
    isRefreshing,
    error: hookError,
    useRealtime, // 即時監聽狀態
    handleCreateCapture,
    handleUpdateCapture,
    handleDeleteCapture,
    refreshCaptures,
    // 分頁相關
    hasMore,
    isLoadingMore,
    loadMoreCaptures,
  } = useCaptures(userId);

  // 本地狀態管理
  const [selectedCapture, setSelectedCapture] = useState<Capture | null>(null);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [modalInitialContent, setModalInitialContent] = useState(''); // Modal 初始內容
  const [inputKey, setInputKey] = useState(0); // 用於強制重新渲染 CaptureInputSection
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 篩選條狀態
  const [isInboxFilterActive, setIsInboxFilterActive] = useState(true);

  // 移除 Header 右側的 Add Capture 按鈕
  const HeaderActions = useMemo(() => [], []);

  const headerConfig = useMemo(() => ({
    title: 'Captures',
    rightActions: HeaderActions,
    subHeader: (
      <CaptureFilterBar
        key={`inbox-${isInboxFilterActive}`}
        isInboxActive={isInboxFilterActive}
        onInboxToggle={setIsInboxFilterActive}
        isLessonMode={showLesson1Guide}
      />
    )
  }), [HeaderActions, isInboxFilterActive]);

  useHeader(headerConfig);

  // 獲取特定 capture（保持原有邏輯，因為需要單獨獲取）
  useEffect(() => {
    if (captureId) {
      fetchSingleCapture(captureId);
    } else {
      setSelectedCapture(null);
    }
  }, [captureId]);

  // 獲取單個 Capture（保持原有邏輯）
  const fetchSingleCapture = async (id: string) => {
    try {
      const capture = await getCaptureById(id);
      if (capture) {
        setSelectedCapture(capture);
      } else {
        setError('找不到指定的收集項目');
      }
    } catch (error) {
      logError('Error fetching single capture:', error);
      setError('載入指定的收集項目失敗');
    }
  };

  /**
   * 處理展開 Capture Modal（從 CaptureInputSection）
   */
  const handleExpandCapture = useCallback((content: string) => {
    setModalInitialContent(content);
    setShowCaptureModal(true);
  }, []);

  /**
   * Capture Modal 提交處理 - 使用 hook 的方法
   */
  const handleCaptureSubmit = async (content: string, relatedToCurrentTimeBox?: boolean) => {
    setIsSubmitting(true);

    try {
      await handleCreateCapture(content);
      setShowCaptureModal(false);
      setModalInitialContent(''); // 清空 Modal 初始內容
      setInputKey((prev: number) => prev + 1); // 強制重新渲染 CaptureInputSection，清空內容
    } catch (error) {
      logError('Error adding capture:', error);
      showError('Failed to Submit', error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quick Capture 提交處理
  const handleQuickCaptureSubmit = async (content: string) => {
    setIsSubmitting(true);

    try {
      await handleCreateCapture(content);
    } catch (error) {
      logError('Error adding quick capture:', error);
      showError('Failed to Submit', error instanceof Error ? error.message : 'An unexpected error occurred');
      throw error; // 讓 CaptureInputSection 可以處理錯誤
    } finally {
      setIsSubmitting(false);
    }
  };

  // 處理返回按鈕點擊
  const handleBackClick = () => {
    setSelectedCapture(null);
    navigate('/captures', { replace: true });
  };

  // 根據 Inbox 篩選狀態決定顯示的 captures
  const displayedCaptures = useMemo(() => {
    // Inbox 篩選：只顯示未處理的 captures（processed = false）
    return isInboxFilterActive 
      ? captures.filter(c => !c.processed)
      : captures;
  }, [captures, isInboxFilterActive]);

  // 處理更新和刪除（使用 hook 方法並刷新）
  const handleUpdate = useCallback(async () => {
    // hook 已經自動更新狀態，但可以手動刷新確保同步
    await refreshCaptures();
  }, [refreshCaptures]);

  const handleDelete = useCallback(async (captureId: string) => {
    // 使用 hook 的刪除方法，它會自動更新本地狀態和同步到伺服器
    return await handleDeleteCapture(captureId);
  }, [handleDeleteCapture]);

  // 渲染載入中、錯誤或空狀態
  const renderContentState = () => {
    const finalError = error || (hookError ? hookError.message : null);
    
    if (isLoading && captures.length === 0) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
        </div>
      );
    }

    if (finalError) {
      return <ErrorState message={finalError} className="mx-4 my-6" />;
    }

    if (captures.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-60">
          <NotebookPen className="h-12 w-12 text-gray-400 mb-3" />
          <p className="text-gray-400 font-medium">No Captures Yet</p>
          <button
            onClick={() => setShowCaptureModal(true)}
            className="btn-accent btn-sm rounded-full mt-4"
          >
            <Plus size={16} />
            Add Capture
          </button>
        </div>
      );
    }


    return null;
  };

  // 直接渲染 Timeline 視圖，不再需要視圖切換
  const renderSelectedView = () => {
    return (
      <CaptureTimelineView
        captures={displayedCaptures}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMoreCaptures}
        isLessonMode={showLesson1Guide}
        lessonSeedCaptureId={lessonSeedCaptureId ?? undefined}
      />
    );
  };

  return (
    <div className="h-full relative pb-20 content-standard">
      {selectedCapture ? (
        // 顯示單個 Capture 的詳細資訊
        <div className="h-full flex flex-col">
          {/* 頂部導航欄 */}
          <div className="flex items-center border-b border-neutral-200 p-3">
            <button
              onClick={handleBackClick}
              className="text-gray-500 hover:text-gray-700 mr-2"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="text-lg font-medium">Capture Details</div>
          </div>

          {/* Capture 內容 */}
          <div className="flex-1 overflow-y-auto hide-scrollbar p-4">
            <div className={`p-4 rounded-lg border ${selectedCapture.processed ? 'border-gray-300 bg-gray-50' : 'border-sky-300 bg-sky-50'}`}>
              <div className="text-sm text-gray-500 mb-1">
                {new Date(selectedCapture.createdAt).toLocaleString()}
              </div>
              <div className="whitespace-pre-wrap">{selectedCapture.content}</div>

              {selectedCapture.convertedToId && (
                <div className="mt-4 p-2 bg-white rounded border border-gray-200">
                  <div className="text-sm text-gray-500">Converted to:</div>
                  <a
                    href={`/tasks?taskId=${selectedCapture.convertedToId}`}
                    className="text-sky-500 hover:text-sky-700"
                  >
                    View Task
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // 顯示 Captures 列表
        <>
          {/* Quick Capture 輸入區 */}
          <CaptureInputSection
            key={inputKey}
            onSubmit={handleQuickCaptureSubmit}
            isSubmitting={isSubmitting}
            onExpand={handleExpandCapture}
          />

          {/* 內容區域 */}
          {renderContentState() || (
            <div className="pb-20">
              {renderSelectedView()}
            </div>
          )}

          {/* Capture 模態框 */}
          {showCaptureModal && (
            <CaptureModal
              isOpen={true}
              onClose={() => {
                setShowCaptureModal(false);
                setModalInitialContent('');
              }}
              onSubmit={handleCaptureSubmit}
              initialContent={modalInitialContent}
              userId={userId}
              isSubmitting={isSubmitting}
            />
          )}
        </>
      )}

      {/* Onboarding Lesson 1 引導（Portal，右下角浮動卡片） */}
      {showLesson1Guide && (
        <Lesson1Guide
          onClose={() => setShowLesson1Guide(false)}
          onSeedCaptureLoaded={setLessonSeedCaptureId}
        />
      )}
    </div>
  );
};

export default Captures;